import { Box, Card, CardContent, Grid, Stack, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useConfig } from "../../contexts/ConfigContext";
import { Config } from "../../types";

type HomePageShortcut = Config["homePageShortcuts"][number];

export default function QuickMenu() {
  const config = useConfig();
  const navigate = useNavigate();

  const allEndpoints = config.apiEndpoints ?? [];

  // Shortcuts authored by the org (admin console → Config → Shortcuts, or config.json)
  const configShortcuts = config.homePageShortcuts ?? [];

  // Shortcuts store the endpoint's `tableName`, which is what `?endpoint=` resolves by.
  // A config authored against the backend's raw slugs can instead carry `spatialdataset`
  // where the app addresses the warehouse as `spatial-datasets`, so fall back to the slug
  // before giving up — otherwise that shortcut silently opened the first endpoint instead.
  const navigateToEndpoint = (tableName: string) => {
    const match = allEndpoints.find((ep) => ep.tableName === tableName) ?? allEndpoints.find((ep) => ep.tableSlug === tableName);
    const target = match?.tableName ?? allEndpoints[0]?.tableName;
    if (target) navigate(`/map?endpoint=${target}`);
  };

  // Tab index -> route mapping (mirrors the old AppContent tab ordering)
  const handleShortcutClick = (card: HomePageShortcut) => {
    if (card.path) {
      navigate(card.path);
      return;
    }
    switch (card.targetTab) {
      case 3:
        navigate("/upload");
        break;
      case 4:
        navigate("/editor");
        break;
      case 5:
        navigate("/tools");
        break;
      default:
        // targetTab 1 (Map) or 2 (Tables) — navigate with endpoint
        if (card.options?.tableName) navigateToEndpoint(card.options.tableName);
    }
  };

  // The grid is exactly what the org authored, plus Media. CM tables are deliberately
  // NOT auto-listed any more: orgs with many tables got an unreadable grid that also
  // duplicated their own authored shortcuts. Media is always offered (when the org has
  // a media endpoint) so the panel never renders empty.
  const mediaEndpoint = allEndpoints.find((ep) => ep.entityType === "media");
  const mediaAlreadyAuthored = configShortcuts.some((s) => !!s.options?.tableName && s.options.tableName === mediaEndpoint?.tableName);

  const cards: Array<{ key: string; name: string; onClick: () => void }> = [
    ...(mediaEndpoint && !mediaAlreadyAuthored
      ? [
          {
            key: "media",
            name: mediaEndpoint.name || "Media",
            onClick: () => navigateToEndpoint(mediaEndpoint.tableName)
          }
        ]
      : []),
    ...configShortcuts.map((shortcut, index) => ({
      key: `config-${index}`,
      name: shortcut.name,
      onClick: () => handleShortcutClick(shortcut)
    }))
  ];

  return (
    <Card
      sx={{
        width: {
          xs: "90%",
          md: "80dvw"
        },
        maxWidth: "80rem",
        minHeight: "240px", // 80% of 300px
        maxHeight: "72dvh", // 80% of 90vh
        backgroundColor: "grey.800",
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.3)",
        borderRadius: "0 0 0.5rem 0.5rem",
        borderTop: "6px solid",
        borderTopColor: "primary.main",
        transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
        overflow: "auto",
        "&:hover": {
          boxShadow: "0 12px 48px rgba(0, 0, 0, 0.4)"
        }
      }}
      className='scroller'
    >
      <CardContent sx={{ p: 0 }}>
        <Grid container spacing={3} alignItems='stretch'>
          <Grid item xs={12} md={5}>
            <Stack
              gap={"1.75rem"}
              sx={{
                padding: {
                  xs: "2rem 1rem",
                  lg: "3.5rem 1.75rem 3.5rem 3.5rem"
                },
                flexDirection: {
                  sm: "row",
                  md: "column"
                }
              }}
            >
              <Box
                sx={{
                  height: "17.5rem",
                  width: "100%",
                  minWidth: { xs: "14rem", sm: "20rem", md: "auto" },
                  maxWidth: {
                    xs: "100%",
                    sm: "20rem",
                    md: "100%"
                  }
                }}
              >
                <img
                  src={config.loginImagePath ?? "icons/lock.png"}
                  alt='login'
                  style={{
                    objectFit: "contain",
                    objectPosition: "center",
                    width: "100%",
                    height: "100%",
                    borderRadius: "0.5rem",
                    aspectRatio: "1/1"
                  }}
                />
              </Box>
              <Typography
                variant='body1'
                align='justify'
                color='grey.100'
                sx={{
                  px: 1.6, // 80% of 2
                  lineHeight: "normal"
                }}
              >
                {config.homePageDescription ?? "Welcome to Culture Map Demo..."}
              </Typography>
            </Stack>
          </Grid>
          <Grid item xs={12} md={7}>
            <Stack
              sx={{
                gap: "2.5rem",
                padding: {
                  xs: "2rem 1rem",
                  lg: "3.5rem 3rem 3.5rem 1.75rem"
                }
              }}
            >
              <Box sx={{ paddingRight: "0.5rem" }}>
                <Typography variant='h2' fontWeight={600} color='grey.25'>
                  Choose how you'd like to explore the database
                </Typography>
              </Box>
              <Grid
                container
                rowSpacing={"2rem"}
                columnSpacing={"1.5rem"}
                className='scroller'
                sx={{
                  maxHeight: "30rem",
                  paddingRight: "0.5rem",
                  overflowY: "auto"
                }}
              >
                {cards.map((card) => (
                  <Grid item xs={6} sm={4} md={6} lg={4} key={card.key}>
                    <Card
                      onClick={card.onClick}
                      elevation={0}
                      sx={{
                        display: "flex",
                        bgcolor: "grey.700",
                        height: { xs: 95, sm: "8em" },
                        width: "100%",
                        padding: "2.375rem 0.275rem",
                        border: "1px solid",
                        borderColor: "grey.600",
                        borderRadius: "12px",
                        cursor: "pointer",
                        justifyContent: "center",
                        alignItems: "center",
                        transition: "all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
                        "&:hover": {
                          color: "primary.main",
                          transform: "translateY(-0.125rem) scale(1.03)",
                          border: "1px solid",
                          borderColor: "primary.main",
                          backgroundColor: "grey.800"
                        },
                        "&:active": {
                          transform: "translateY(-3px) scale(1.03)",
                          boxShadow: "0 8px 24px rgba(0, 0, 0, 0.3)",
                          transition: "all 0.1s ease-out"
                        }
                      }}
                    >
                      <CardContent
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          height: "100%",
                          padding: "16px !important"
                        }}
                      >
                        <Typography
                          variant='h4'
                          align='center'
                          sx={{
                            color: "grey.25",
                            wordBreak: "break-word",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            fontSize: { xs: "0.76rem", sm: "0.84rem", md: "0.92rem" },
                            fontWeight: 500,
                            letterSpacing: "0.01em",
                            lineHeight: 1.2
                          }}
                        >
                          {card.name}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}

                {cards.length === 0 && (
                  <Grid item xs={12}>
                    <Typography variant='body2' color='grey.400' sx={{ px: 1 }}>
                      No shortcuts configured for your organisation.
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Stack>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
